import React, {useEffect, useMemo} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {useConfig} from '@lore/config';

/**
 * @typedef {Object} ProjectMetricInvitationItem
 * @property {number} id
 * @property {number} projectId
 * @property {number} projectCreatorId
 * @property {number} creatorId
 * @property {string} request
 * @property {string} response
 * @property {string} responseMessage
 * @property {string} result
 * @property {string} createdAt
 * @property {number} siteId
 */

/**
 * @typedef {Object} ProjectMetricInvitationsResponse
 * @property {number} total
 * @property {number} page
 * @property {number} pageSize
 * @property {number} networkId
 * @property {ProjectMetricInvitationItem[]} data
 */

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {ProjectMetricInvitationItem}
 */
export const _ProjectMetricInvitationItem = {};

/**
 * dummy export to allow JSDoc types to be imported by other files
 * @type {ProjectMetricInvitationsResponse}
 */
export const _ProjectMetricInvitationsResponse = {};

const STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Custom hook to fetch project metric invitations from the core API using infinite scrolling.
 *
 * @param {Object} [params={}] - Query parameters for the request.
 * @param {number|string} [params.networkId] - The ID of the network.
 * @param {string} [params.startDate] - Optional ISO date string for the start of the range.
 * @param {string} [params.endDate] - Optional ISO date string for the end of the range.
 * @param {number} [params.pageSize=500] - The number of items per page.
 * @param {Object} [options={}] - Configuration options like 'enabled' to control the query.
 * @returns {import('@tanstack/react-query').UseInfiniteQueryResult<ProjectMetricInvitationsResponse> & {data: ProjectMetricInvitationItem[]}} React Query result object.
 */
export default function useCoreProjectsMetricsInvitations(
  {networkId, startDate, endDate, pageSize = 500} = {},
  {enabled = true} = {}
) {
  const config = useConfig();
  const {apiRoot, headers} = config.connections.default;

  const query = useInfiniteQuery({
    queryKey: ['project', 'core', 'metrics', 'invitations', {networkId, startDate, endDate, pageSize}],

    queryFn: ({pageParam = 1}) => {
      const url = new URL(`${apiRoot}/core/projects/metrics/invitations`);

      if (networkId) url.searchParams.append('networkId', String(networkId));
      if (startDate) url.searchParams.append('startDate', startDate);
      if (endDate) url.searchParams.append('endDate', endDate);
      url.searchParams.append('page', String(pageParam));
      url.searchParams.append('pageSize', String(pageSize));

      return fetch(url.toString(), {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch project metric invitations');
        }
        return res.json();
      });
    },

    getNextPageParam: (lastPage) => {
      const {page, pageSize, total} = lastPage;
      const totalPages = Math.ceil(total / pageSize);
      return page < totalPages ? page + 1 : undefined;
    },

    staleTime: STALE_TIME,
    enabled: enabled,
  });

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && enabled) {
      fetchNextPage().catch(()=> console.error('Failed to fetch next page of invitations'));
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, enabled]);

  const allData = useMemo(() => {
    return data ? data.pages.flatMap((page) => page.data) : [];
  }, [data]);

  return {
    ...query,
    data: allData,
    isLoading: isLoading || isFetchingNextPage || hasNextPage,
  };
}
